// ChecklistTemplateItems.js
// Type-owned checklist template items (v3) + sync helpers for node instances.

import {
  createEnumOptionId,
  generateEnumOptionValue,
} from 'utilities/EnumOptions';
import { normalizeChecklistItemActions } from 'utilities/ChecklistItemActions';
import { normalizeChecklistItemResources } from 'utilities/ChecklistItemResources';

const normalizeString = (value) => String(value || '').trim();

/**
 * Stored accomplishment continuation (no actor, no trailing period).
 *
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeAccomplishmentPhrase(value) {
  const text = normalizeString(value)
    .replace(/^["'`]+|["'`]+$/g, '')
    .replace(/\.+$/, '')
    .trim();
  if (!text) return '';
  return text.charAt(0).toLowerCase() + text.slice(1);
}

/**
 * @param {object|null|undefined} item
 * @returns {string}
 */
export function getChecklistTemplateItemAccomplishmentPhrase(item) {
  if (!item || typeof item !== 'object') return '';
  return normalizeAccomplishmentPhrase(item.accomplishmentPhrase);
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function normalizeAllowCustomChecklistItems(value) {
  return value === true;
}

/**
 * Resolve display label from a template item or snapshot item.
 *
 * @param {object|null|undefined} item
 * @returns {string}
 */
export function getChecklistTemplateItemLabel(item) {
  if (!item || typeof item !== 'object') return '';
  const fromTranslations = normalizeString(item?.translations?.en?.label);
  if (fromTranslations) return fromTranslations;
  return normalizeString(item?.label || item?.text || item?.key || item?.value);
}

/**
 * Normalize schema checklistItems into canonical objects.
 *
 * @param {unknown} items
 * @returns {Array<object>}
 */
export function normalizeChecklistTemplateItems(items) {
  if (!Array.isArray(items)) return [];

  const result = [];
  const seenKeys = new Set();

  items.forEach((raw, index) => {
    if (raw == null) return;

    if (typeof raw === 'string') {
      const label = normalizeString(raw);
      if (!label) return;
      const key = uniqueTemplateKey(generateEnumOptionValue(label), seenKeys);
      seenKeys.add(key);
      result.push({
        id: createEnumOptionId(),
        key,
        translations: { en: { label } },
        sortOrder: index,
      });
      return;
    }

    if (typeof raw !== 'object') return;

    const label = getChecklistTemplateItemLabel(raw) || 'Item';
    const keyCandidate = normalizeString(raw.key || raw.value) || generateEnumOptionValue(label);
    const key = seenKeys.has(keyCandidate)
      ? uniqueTemplateKey(keyCandidate, seenKeys)
      : keyCandidate;
    seenKeys.add(key);

    const id = normalizeString(raw.id) || createEnumOptionId();
    const actions = normalizeChecklistItemActions(raw.actions);
    const resources = normalizeChecklistItemResources(raw.resources);
    const accomplishmentPhrase = normalizeAccomplishmentPhrase(raw.accomplishmentPhrase);
    result.push({
      id,
      key,
      translations: {
        en: { label },
      },
      sortOrder: Number.isFinite(Number(raw.sortOrder)) ? Number(raw.sortOrder) : index,
      ...(actions.length ? { actions } : {}),
      ...(resources.length ? { resources } : {}),
      ...(accomplishmentPhrase ? { accomplishmentPhrase } : {}),
    });
  });

  return result;
}

/**
 * @param {string} base
 * @param {Set<string>} seenKeys
 * @returns {string}
 */
function uniqueTemplateKey(base, seenKeys) {
  const root = normalizeString(base) || 'item';
  if (!seenKeys.has(root)) return root;
  let counter = 2;
  while (seenKeys.has(`${root}_${counter}`)) counter += 1;
  return `${root}_${counter}`;
}

/**
 * Build seed snapshot items from a field's template (all todo).
 *
 * @param {object|null|undefined} field
 * @returns {Array<object>}
 */
export function buildSeedItemsFromChecklistField(field) {
  const templateItems = normalizeChecklistTemplateItems(field?.checklistItems);
  return templateItems.map((item, index) => {
    const accomplishmentPhrase = getChecklistTemplateItemAccomplishmentPhrase(item);
    return {
      key: item.key,
      text: getChecklistTemplateItemLabel(item),
      position: index,
      status: 'todo',
      source: 'template',
      templateKey: item.key,
      ...(accomplishmentPhrase ? { accomplishmentPhrase } : {}),
    };
  });
}

/**
 * Whether a snapshot item is a type-level (template) item.
 *
 * @param {object|null|undefined} item
 * @param {Set<string>|null} [templateKeySet]
 * @returns {boolean}
 */
export function isTemplateChecklistItem(item, templateKeySet = null) {
  if (!item || typeof item !== 'object') return false;
  if (normalizeString(item.source).toLowerCase() === 'custom') return false;
  if (normalizeString(item.source).toLowerCase() === 'template') return true;

  const key = normalizeString(item.templateKey || item.key);
  if (templateKeySet && templateKeySet.size > 0) {
    return Boolean(key && templateKeySet.has(key));
  }

  // No template on the field: treat all items as freeform (not schema-required).
  return false;
}

/**
 * Merge schema template into an existing item list (lazy sync).
 *
 * @param {object} params
 * @param {unknown} params.existingItems
 * @param {object|null|undefined} params.field
 * @returns {{ items: object[], changed: boolean }}
 */
export function syncChecklistItemsWithTemplate({ existingItems, field } = {}) {
  const templateItems = normalizeChecklistTemplateItems(field?.checklistItems);
  const templateKeySet = new Set(templateItems.map((item) => item.key));
  const previous = Array.isArray(existingItems) ? existingItems : [];

  const previousByKey = new Map();
  previous.forEach((item) => {
    const key = normalizeString(item?.key || item?.templateKey);
    if (key) previousByKey.set(key, item);
  });

  const nextTemplateRows = templateItems.map((templateItem, index) => {
    const existing = previousByKey.get(templateItem.key);
    const label = getChecklistTemplateItemLabel(templateItem);
    const accomplishmentPhrase = getChecklistTemplateItemAccomplishmentPhrase(templateItem);
    if (!existing) {
      return {
        key: templateItem.key,
        text: label,
        position: index,
        status: 'todo',
        completedAt: null,
        assignees: [],
        source: 'template',
        templateKey: templateItem.key,
        ...(accomplishmentPhrase ? { accomplishmentPhrase } : {}),
      };
    }

    return {
      ...existing,
      key: templateItem.key,
      text: label,
      position: index,
      status: existing.status === 'done' ? 'done' : 'todo',
      source: 'template',
      templateKey: templateItem.key,
      assignees: Array.isArray(existing.assignees) ? existing.assignees : [],
      ...(accomplishmentPhrase
        ? { accomplishmentPhrase }
        : {}),
    };
  });

  const customRows = previous
    .filter((item) => {
      const key = normalizeString(item?.key || item?.templateKey);
      if (templateKeySet.has(key)) return false;
      if (normalizeString(item?.source).toLowerCase() === 'template') return false;
      // Legacy freeform items (no source) that aren't template keys → custom
      return true;
    })
    .map((item, index) => ({
      ...item,
      source: 'custom',
      position: templateItems.length + index,
      text: normalizeString(item?.text) || 'Item',
      status: item?.status === 'done' ? 'done' : 'todo',
      assignees: Array.isArray(item?.assignees) ? item.assignees : [],
    }));

  const nextItems = [...nextTemplateRows, ...customRows].map((item, index) => ({
    ...item,
    position: index,
  }));

  const changed = !checklistItemListsEqual(previous, nextItems);
  return { items: nextItems, changed };
}

/**
 * @param {unknown} left
 * @param {unknown} right
 * @returns {boolean}
 */
function checklistItemListsEqual(left, right) {
  const a = Array.isArray(left) ? left : [];
  const b = Array.isArray(right) ? right : [];
  if (a.length !== b.length) return false;

  for (let index = 0; index < a.length; index += 1) {
    const leftItem = a[index] || {};
    const rightItem = b[index] || {};
    if (normalizeString(leftItem.key) !== normalizeString(rightItem.key)) return false;
    if (normalizeString(leftItem.text) !== normalizeString(rightItem.text)) return false;
    if (normalizeString(leftItem.status) !== normalizeString(rightItem.status)) return false;
    if (normalizeString(leftItem.source) !== normalizeString(rightItem.source)) return false;
    if (Number(leftItem.position) !== Number(rightItem.position)) return false;
  }

  return true;
}

/**
 * Apply checklist template props onto a field object for persistence.
 *
 * @param {object} nextField
 * @param {object} source
 * @returns {object}
 */
export function applyChecklistTemplateFieldProps(nextField, source = {}) {
  if (!nextField || typeof nextField !== 'object') return nextField;

  const checklistItems = normalizeChecklistTemplateItems(
    source.checklistItems ?? nextField.checklistItems,
  );
  const allowCustomItems = normalizeAllowCustomChecklistItems(
    Object.prototype.hasOwnProperty.call(source, 'allowCustomItems')
      ? source.allowCustomItems
      : nextField.allowCustomItems,
  );

  if (checklistItems.length) {
    nextField.checklistItems = checklistItems;
  } else {
    delete nextField.checklistItems;
  }

  if (allowCustomItems) {
    nextField.allowCustomItems = true;
  } else {
    delete nextField.allowCustomItems;
  }

  delete nextField.templateKeys;
  return nextField;
}
